/**
 * Drawing the game on screen, using the browser's 2D canvas.
 *
 * Every frame, the whole picture is redrawn from scratch, back to front: track,
 * grid, tray, columns, the blocks on the track, effects, then text. Redrawing
 * everything each frame is simpler than tracking what changed, and fast
 * enough for a game this size.
 *
 * The renderer reads the game state but doesn't change it. Animations (shots,
 * pops, blocks flying onto the track, tray slides) are tracked here, separate
 * from the rules, so the rules stay simple and testable. One small exception:
 * `launchDelay()` tells the game how long a launched block should pause, so
 * the block starts moving exactly when its flight animation lands.
 *
 * Colors come from the current theme (`THEMES` in palette.ts), switched with
 * `setTheme`. Nothing in here hard-codes a color, so the same drawing code
 * serves both the light and dark themes.
 */
import { ClearEffects, type Pop, RetireEffects } from "./effects";
import {
	computeLayout,
	cornerSlots,
	DEFAULT_LAYOUT,
	gridToPixel,
	type Layout,
	type LayoutSettings,
	slotToPixel,
	trackToPixel,
} from "./layout";
import {
	type Shape,
	swatchFor,
	THEMES,
	type Theme,
	type ThemeName,
} from "./palette";
import { drawShape, drawTile } from "./shapes";
import {
	type Block,
	canLaunch,
	type GameState,
	planLaunch,
	type Runner,
	type Source,
} from "./state";

type Point = { x: number; y: number };

/** A block with its position on screen for this frame, ready to draw. */
interface Placed {
	block: Block;
	x: number;
	y: number;
	alpha: number;
	onTrack: boolean;
	/**
	 * How big to draw it, in pixels. Blocks in the tray and columns use the
	 * click-target size, blocks on the track a smaller size in proportion to
	 * the grid; a block flying onto the track shrinks from one to the other.
	 */
	size: number;
}

/** A block flying from where it was clicked onto the track. */
interface Flight {
	from: { x: number; y: number };
	/**
	 * How long the block's pause at the start of the track was. Comparing this
	 * with the time it has left gives how far through the flight it is.
	 */
	duration: number;
}

/** A block flying from the track into its tray slot after finishing a lap. */
interface TrayFlight {
	from: { x: number; y: number };
	/** When it left the track, in milliseconds (from `performance.now()`). */
	start: number;
}

/**
 * How much animation to show. "system" follows the player's operating-system
 * setting for reduced motion; "full" and "reduced" override it (for testing).
 */
export type MotionPreference = "system" | "full" | "reduced";

/**
 * Settings that change how the game looks. They take effect on the next frame
 * and never change how the game plays. Includes the layout settings
 * (`extends LayoutSettings`).
 */
export interface RenderSettings extends LayoutSettings {
	/**
	 * Width of the gap in the track at its bottom-left corner, in cells. Blocks
	 * disappear into the gap. 0 closes the track into a full loop.
	 */
	cutSize: number;
	/** How long the pop animation of a cleared cell lasts, in milliseconds. */
	popMs: number;
	/** How many small particles burst out of each cleared cell. */
	particles: number;
	/** How fast the shot travels from a block to the cell it clears, in cells per second. */
	shotSpeed: number;
	/** How long the shot's trail can be, in cells. */
	shotTrail: number;
	/**
	 * How long a clicked block takes to fly from its column or tray slot onto the
	 * track, in milliseconds. It pauses at the start of the track until it lands.
	 */
	flyMs: number;
	/** How long tray blocks take to slide left after a gap opens, in milliseconds. */
	slideMs: number;
	/** How see-through (opacity, 0 to 1) to draw blocks that can't be launched right now. */
	dimmed: number;
	/** Whether to follow the player's reduced-motion setting or override it. */
	motion: MotionPreference;
}

/** The default visual settings. */
export const DEFAULT_RENDER_SETTINGS: RenderSettings = {
	...DEFAULT_LAYOUT,
	popMs: 420,
	particles: 6,
	shotSpeed: 50, // about 5x a runner's default speed
	shotTrail: 1.2,
	cutSize: 1,
	flyMs: 260,
	slideMs: 180,
	dimmed: 0.55,
	motion: "system",
};

/** How long a cleared cell takes to fade out when motion is reduced, in milliseconds. */
const POP_REDUCED_MS = 200;

/**
 * The text shown around the board. main.ts provides this, because it knows
 * what's being played (a numbered level, a custom level, an image).
 */
export interface Hud {
	/** Shown above the board, e.g. "Level 7 · Medium". */
	title: string;
	/** The big message when the level is won, e.g. "Level 7 cleared!". */
	won: string;
	/** The smaller line under the win or loss message, saying what a click will do next. */
	wonHint: string;
	lostHint: string;
}

const DEFAULT_HUD: Hud = {
	title: "",
	won: "Cleared!",
	wonHint: "Click to play again",
	lostHint: "Click to play again",
};

/**
 * Draws the game onto a canvas element.
 *
 * The canvas fills its box on the page. On high-resolution ("retina")
 * screens, one CSS pixel is several real pixels, so the canvas is given
 * extra real pixels (see `resize`) to keep everything sharp.
 */
export class Renderer {
	private readonly ctx: CanvasRenderingContext2D;
	private readonly effects = new ClearEffects(0, 0);
	/** Used-up blocks popping as they leave the track. */
	private readonly retired = new RetireEffects(0);
	private readonly systemMotion = window.matchMedia(
		"(prefers-reduced-motion: reduce)",
	);
	private reducedMotion = false;
	private settings: RenderSettings = { ...DEFAULT_RENDER_SETTINGS };
	/** The current theme's colors (see palette.ts). Changed with `setTheme`. */
	private colors: Theme = THEMES.dark;
	/** The block chosen with the keyboard, outlined so it can be seen; null for none. */
	private selection: Source | null = null;
	/**
	 * Where each tray block was last drawn, so it can slide smoothly when its slot
	 * changes. A `WeakMap` is used so that once a block is gone (nothing refers to
	 * it any more), its entry is cleaned up automatically.
	 */
	private trayX = new WeakMap<Block, number>();
	private lastFrame = performance.now();
	/** Blocks on the track that are still flying in from where they were clicked. */
	private flights = new WeakMap<Runner, Flight>();
	/** Tray blocks still flying in from the track. */
	private trayFlights = new WeakMap<Block, TrayFlight>();
	/** Blocks on the track already matched to the click that launched them, so each flight starts only once. */
	private seenRunners = new WeakSet<Runner>();
	private flightState: GameState | null = null;
	private eventsSeen = 0;

	constructor(private readonly canvas: HTMLCanvasElement) {
		const ctx = canvas.getContext("2d");
		if (!ctx) throw new Error("2D canvas context not available");
		this.ctx = ctx;
		this.configure(this.settings);
		this.systemMotion.addEventListener("change", () =>
			this.configure(this.settings),
		);
		this.resize();
		// Watch the canvas element itself for size changes (not just the window), so
		// it also resizes when the dev panel opens or closes.
		new ResizeObserver(() => this.resize()).observe(canvas);
	}

	/**
	 * Apply new visual settings, and work out again whether motion should be
	 * reduced (the player's system setting may have changed).
	 */
	configure(settings: RenderSettings): void {
		this.settings = { ...settings };
		const { motion } = settings;
		this.reducedMotion =
			motion === "system" ? this.systemMotion.matches : motion === "reduced";
		this.effects.duration = this.reducedMotion
			? Math.min(POP_REDUCED_MS, settings.popMs)
			: settings.popMs;
		this.retired.duration = this.effects.duration;
		// Shots are pure movement, so with reduced motion they're turned off entirely.
		this.effects.shotSpeed = this.reducedMotion ? 0 : settings.shotSpeed / 1000;
	}

	/**
	 * Show (or, with null, hide) the keyboard selection: a focus ring around the
	 * chosen column's front block or tray slot. See keyboard.ts.
	 */
	setSelection(selection: Source | null): void {
		this.selection = selection;
	}

	/** Switch between the light and dark themes. Takes effect on the next frame. */
	setTheme(theme: ThemeName): void {
		this.colors = THEMES[theme];
	}

	/**
	 * Make the canvas's real pixel size match its size on the page times the
	 * screen's pixel density ("device pixel ratio"), then scale drawing so the
	 * rest of the code can keep working in CSS pixels.
	 */
	resize(): void {
		const dpr = window.devicePixelRatio || 1;
		const { clientWidth, clientHeight } = this.canvas;
		this.canvas.width = Math.round(clientWidth * dpr);
		this.canvas.height = Math.round(clientHeight * dpr);
		this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
	}

	/**
	 * Where everything is on screen right now. main.ts also uses this to work out
	 * what was clicked.
	 */
	layout(state: GameState): Layout {
		return computeLayout(
			state,
			this.canvas.clientWidth,
			this.canvas.clientHeight,
			this.settings,
		);
	}

	/**
	 * Draw one complete frame. Things drawn later appear on top, so the order
	 * below is back to front.
	 */
	render(state: GameState, hud: Hud = DEFAULT_HUD): void {
		const { ctx } = this;
		const w = this.canvas.clientWidth;
		const h = this.canvas.clientHeight;
		const layout = this.layout(state);

		// Clear last frame's drawing, leaving the canvas transparent: the page's
		// background color (`--bg` in src/theme.css) shows through.
		ctx.clearRect(0, 0, w, h);

		this.drawTrack(layout);
		this.drawGrid(state, layout);
		this.drawPops(state, layout);
		this.drawTraySlots(state, layout);
		const placed = this.placeBlocks(state, layout);
		const offTrack = placed.filter((p) => !p.onTrack);
		const onTrack = placed.filter((p) => p.onTrack);
		this.drawLinks(offTrack, layout);
		this.drawPlaced(offTrack);
		this.drawOverflow(state, layout);
		// Blocks on the track are hidden while they pass through the gap at the
		// track's start. `save` and `restore` limit that hiding to just these blocks.
		ctx.save();
		this.clipOutGap(state, layout);
		this.drawLinks(onTrack, layout);
		this.drawPlaced(onTrack);
		this.drawRetired(state, layout);
		ctx.restore();
		this.drawSelection(layout);
		this.drawHud(state, layout);
		this.drawTitle(hud.title, layout);
		if (state.status !== "playing") this.drawStatus(state, hud, w, h);
	}

	/** Whether the track is a full loop, or has a gap at its start corner. */
	private trackClosed(): boolean {
		return this.settings.cutSize <= 0;
	}

	/**
	 * Draw the track: a band one cell wide following the same path the blocks
	 * take, with rounded corners and (unless it's a full loop) a gap at the start.
	 */
	private drawTrack(layout: Layout): void {
		const { ctx } = this;
		const { cell, gridLeft, gridTop, gridWidth, gridHeight } = layout;
		const [start, ...rest] = cornerSlots(gridWidth, gridHeight).map((slot) =>
			slotToPixel(layout, slot),
		);
		const corners = rest as [Point, Point, Point];
		const c0 = start as Point;
		// How round the corners are: the distance from a corner to the slots next to it.
		const radius = c0.y - slotToPixel(layout, -1).y;
		const half = (this.settings.cutSize * layout.trackWidth) / 2;

		ctx.beginPath();
		if (this.trackClosed()) ctx.moveTo(c0.x + radius, c0.y);
		else ctx.moveTo(c0.x + half, c0.y);
		// The corners, in drawing order. `arcTo` draws a straight line toward a corner
		// and then curves around it onto the next side.
		const [br, tr, tl] = corners;
		ctx.arcTo(br.x, br.y, br.x, tr.y, radius);
		ctx.arcTo(tr.x, tr.y, tl.x, tr.y, radius);
		ctx.arcTo(tl.x, tl.y, tl.x, c0.y, radius);
		if (this.trackClosed()) {
			ctx.arcTo(c0.x, c0.y, br.x, c0.y, radius);
			ctx.closePath();
		} else {
			ctx.lineTo(c0.x, c0.y - half);
		}
		ctx.strokeStyle = this.colors.track;
		ctx.lineWidth = layout.trackWidth;
		ctx.lineCap = "butt";
		ctx.lineJoin = "round";
		ctx.stroke();

		ctx.fillStyle = this.colors.gridBackground;
		ctx.fillRect(gridLeft, gridTop, gridWidth * cell, gridHeight * cell);
	}

	/**
	 * Stop anything from being drawn inside the gap at the track's start.
	 * `clip` with the "evenodd" rule keeps the whole canvas except the gap.
	 */
	private clipOutGap(state: GameState, layout: Layout): void {
		if (this.trackClosed()) return;
		const { ctx } = this;
		const corner = gridToPixel(layout, -1, state.grid.height);
		const half = (this.settings.cutSize * layout.trackWidth) / 2;
		// Extend the hidden area past the track's outer edge so a block can't peek out around the side of the gap.
		const reach = layout.trackWidth;
		ctx.beginPath();
		ctx.rect(0, 0, this.canvas.clientWidth, this.canvas.clientHeight);
		ctx.rect(
			corner.x - half - reach,
			corner.y - half,
			half * 2 + reach,
			half * 2 + reach,
		);
		ctx.clip("evenodd");
	}

	/** Draw a list of positioned blocks. Blocks on the track get an outline so they stand out against the grid. */
	private drawPlaced(placed: Placed[]): void {
		const { ctx } = this;
		for (const p of placed) {
			ctx.globalAlpha = p.alpha;
			this.drawBlock(p.block, p.x, p.y, p.size, p.onTrack);
		}
		ctx.globalAlpha = 1;
	}

	/** Draw the grid's remaining cells. */
	private drawGrid(state: GameState, layout: Layout): void {
		const { width, cells } = state.grid;
		cells.forEach((color, i) => {
			if (color === null) return;
			const { x, y } = gridToPixel(layout, i % width, Math.floor(i / width));
			const swatch = swatchFor(color);
			this.drawCell(swatch.fill, swatch.shape, x, y, layout.cell);
		});
	}

	/**
	 * Draw cleared cells disappearing: a shot flies from the block to the cell,
	 * then the cell swells slightly and shrinks away while a ring expands and a
	 * few particles burst out. With reduced motion, the cell just fades.
	 */
	private drawPops(state: GameState, layout: Layout): void {
		const { ctx } = this;
		const now = performance.now();
		this.effects.sync(state, now);
		const { cell } = layout;
		const { width } = state.grid;

		for (const { pop, shot, t } of this.effects.active(now)) {
			const { x, y } = gridToPixel(
				layout,
				pop.index % width,
				Math.floor(pop.index / width),
			);
			const swatch = swatchFor(pop.color);
			const fade = 1 - t;

			if (shot < 1) {
				// The cell stays visible until the shot reaches it.
				this.drawCell(swatch.fill, swatch.shape, x, y, cell);
				this.drawShot(pop, shot, swatch.fill, layout);
				continue;
			}

			if (this.reducedMotion) {
				ctx.globalAlpha = fade;
				this.drawCell(swatch.fill, swatch.shape, x, y, cell);
				ctx.globalAlpha = 1;
				continue;
			}

			// Grow to 115% during the first fifth of the animation, then shrink to nothing.
			const scale =
				t < 0.2 ? 1 + 0.75 * t : 1.15 * (1 - easeIn((t - 0.2) / 0.8));
			ctx.globalAlpha = fade;
			this.drawCell(swatch.fill, swatch.shape, x, y, cell * scale);

			ctx.strokeStyle = this.colors.text;
			ctx.lineWidth = cell * 0.08 * fade;
			ctx.beginPath();
			ctx.arc(x, y, cell * (0.3 + 0.6 * easeOut(t)), 0, Math.PI * 2);
			ctx.stroke();

			ctx.fillStyle = swatch.fill;
			const spread = cell * 0.9 * easeOut(t);
			const bit = cell * 0.14 * fade;
			for (let i = 0; i < this.settings.particles; i++) {
				// Rotate the burst a little depending on the cell, so neighbouring pops don't look identical.
				const angle =
					((i + (pop.index % 3) / 3) * Math.PI * 2) / this.settings.particles;
				const px = x + Math.cos(angle) * spread;
				const py = y + Math.sin(angle) * spread;
				ctx.fillRect(px - bit / 2, py - bit / 2, bit, bit);
			}
			ctx.globalAlpha = 1;
		}
	}

	/**
	 * Draw used-up blocks leaving the track as a star burst: the block shrinks
	 * away into a spinning star in its color, rays shoot outward, and small
	 * stars sparkle at their tips, all fading out. With reduced motion, the
	 * block just fades.
	 */
	private drawRetired(state: GameState, layout: Layout): void {
		const { ctx } = this;
		const now = performance.now();
		this.retired.sync(state, now);
		for (const { pop, t } of this.retired.active(now)) {
			// Most leave from the track; a used-up linked block waiting in the tray
			// leaves from its tray slot (see `RetiredBlock`).
			const { x, y } =
				pop.from === "track"
					? trackToPixel(layout, pop.position, this.trackClosed())
					: {
							x: layout.trayCenters[pop.slot] ?? 0,
							y: layout.trayTop + layout.blockSize / 2,
						};
			const size =
				pop.from === "track" ? layout.trackBlockSize : layout.blockSize;
			const fade = 1 - t;
			if (this.reducedMotion) {
				ctx.globalAlpha = fade;
				this.drawBlock({ color: pop.color, count: 0 }, x, y, size, true);
				ctx.globalAlpha = 1;
				continue;
			}
			const { fill } = swatchFor(pop.color);
			const burst = easeOut(t);
			// Stay bright for most of the burst, fading mostly at the end.
			const glow = 1 - easeIn(t);

			// The block shrinks away over the first quarter.
			if (t < 0.25) {
				const shrink = 1 - t / 0.25;
				this.drawBlock(
					{ color: pop.color, count: 0 },
					x,
					y,
					size * shrink,
					true,
				);
			}

			ctx.globalAlpha = glow;
			// Rays: eight lines shooting outward, alternating long and short. Each is
			// a streak whose inner end follows its outer end, so it flies off.
			const rays = 8;
			ctx.lineCap = "round";
			for (let i = 0; i < rays; i++) {
				const angle = (i * Math.PI * 2) / rays + Math.PI / rays;
				const reach = size * (i % 2 === 0 ? 1.7 : 1.15);
				const outer = reach * burst;
				const inner = reach * Math.max(0, burst - 0.35);
				const cos = Math.cos(angle);
				const sin = Math.sin(angle);
				ctx.strokeStyle = fill;
				ctx.lineWidth = Math.max(2, size * 0.16 * fade);
				ctx.beginPath();
				ctx.moveTo(x + cos * inner, y + sin * inner);
				ctx.lineTo(x + cos * outer, y + sin * outer);
				ctx.stroke();
				// A sparkle at the tip of each long ray.
				if (i % 2 === 0)
					this.drawStar(
						x + cos * outer,
						y + sin * outer,
						size * 0.22 * fade,
						t * Math.PI,
						fill,
					);
			}

			// The star in the middle: grows quickly, spins, then shrinks away.
			const grow = t < 0.3 ? easeOut(t / 0.3) : 1 - easeIn((t - 0.3) / 0.7);
			this.drawStar(x, y, size * 0.85 * grow, t * Math.PI * 0.8, fill);
			ctx.globalAlpha = 1;
		}
	}

	/**
	 * Draw a five-pointed star centered on (`cx`, `cy`), `radius` to its points,
	 * turned by `turn` radians, filled with `fill` and outlined in the text color.
	 */
	private drawStar(
		cx: number,
		cy: number,
		radius: number,
		turn: number,
		fill: string,
	): void {
		if (radius <= 0.5) return;
		const { ctx } = this;
		ctx.beginPath();
		for (let i = 0; i < 10; i++) {
			// Alternate between the points and the inner corners between them.
			const r = i % 2 === 0 ? radius : radius * 0.45;
			const angle = turn - Math.PI / 2 + (i * Math.PI) / 5;
			ctx.lineTo(cx + Math.cos(angle) * r, cy + Math.sin(angle) * r);
		}
		ctx.closePath();
		ctx.fillStyle = fill;
		ctx.fill();
		ctx.strokeStyle = this.colors.text;
		ctx.lineWidth = Math.max(1, radius * 0.12);
		ctx.lineJoin = "round";
		ctx.stroke();
	}

	/**
	 * Draw the shot: a short streak in the block's color with a core in the text color,
	 * `progress` (0 to 1) of the way from the block to the cell.
	 */
	private drawShot(
		pop: Pop,
		progress: number,
		fill: string,
		layout: Layout,
	): void {
		const { ctx } = this;
		const { cell } = layout;
		const dx = pop.to.x - pop.from.x;
		const dy = pop.to.y - pop.from.y;
		const distance = Math.hypot(dx, dy);
		const travelled = distance * progress;
		const trail = Math.min(travelled, this.settings.shotTrail);
		const head = gridToPixel(
			layout,
			pop.from.x + (dx * travelled) / distance,
			pop.from.y + (dy * travelled) / distance,
		);
		const tail = gridToPixel(
			layout,
			pop.from.x + (dx * (travelled - trail)) / distance,
			pop.from.y + (dy * (travelled - trail)) / distance,
		);

		ctx.lineCap = "round";
		ctx.strokeStyle = fill;
		ctx.lineWidth = cell * 0.2;
		ctx.beginPath();
		ctx.moveTo(tail.x, tail.y);
		ctx.lineTo(head.x, head.y);
		ctx.stroke();
		ctx.strokeStyle = this.colors.text;
		ctx.lineWidth = cell * 0.07;
		ctx.stroke();
		ctx.fillStyle = this.colors.text;
		ctx.beginPath();
		ctx.arc(head.x, head.y, cell * 0.11, 0, Math.PI * 2);
		ctx.fill();
	}

	/** Draw one cell of size `size`, centered on (`cx`, `cy`): a rounded colored square with its icon. */
	private drawCell(
		fill: string,
		shape: Shape,
		cx: number,
		cy: number,
		size: number,
	): void {
		drawTile(this.ctx, { name: "", fill, shape }, cx, cy, size, this.colors);
	}

	/** Outline the empty tray slots, so players can see how much room is left. */
	private drawTraySlots(state: GameState, layout: Layout): void {
		const { ctx } = this;
		const size = layout.blockSize;
		const cy = layout.trayTop + size / 2;
		ctx.strokeStyle = this.colors.trackEdge;
		ctx.lineWidth = 2;
		state.tray.forEach((block, i) => {
			if (block) return;
			const cx = layout.trayCenters[i] ?? 0;
			roundRect(ctx, cx - size / 2, cy - size / 2, size, size, size * 0.2);
			ctx.stroke();
		});
	}

	/**
	 * Work out where every visible block goes this frame (tray, columns, track),
	 * in the order they should be drawn. Blocks that can't be launched right now
	 * are marked to be drawn faded.
	 */
	private placeBlocks(state: GameState, layout: Layout): Placed[] {
		const size = layout.blockSize;
		const placed: Placed[] = [];
		const { dimmed } = this.settings;
		// Blocks that can be launched are drawn at full strength; everything else
		// is faded. This uses the same `planLaunch` the rules use, so the hint is
		// always right.
		const alphaFor = (from: "column" | "tray", index: number) =>
			planLaunch(state, { from, index }) ? 1 : dimmed;

		const now = performance.now();
		const dt = now - this.lastFrame;
		this.lastFrame = now;
		this.startFlights(state, layout, now);
		state.tray.forEach((block, i) => {
			if (!block) return;
			const slot = {
				x: this.slideTo(block, layout.trayCenters[i] ?? 0, dt),
				y: layout.trayTop + size / 2,
			};
			placed.push({
				block,
				...this.flyToTray(block, slot, layout, now),
				alpha: alphaFor("tray", i),
				onTrack: false,
			});
		});
		state.columns.forEach((blocks, c) => {
			// Only the first few blocks of each column are drawn (see `drawOverflow`).
			blocks.slice(0, layout.shownDepth).forEach((block, i) => {
				placed.push({
					block,
					x: layout.columnCenters[c] ?? 0,
					y: layout.columnsTop + i * layout.blockPitch + size / 2,
					size,
					alpha: i === 0 ? alphaFor("column", c) : dimmed,
					onTrack: false,
				});
			});
		});
		for (const runner of [...state.runners].sort((a, b) => a.trail - b.trail)) {
			const onTrack = trackToPixel(layout, runner.position, this.trackClosed());
			const { x, y, size } = this.fly(runner, onTrack, layout);
			placed.push({
				block: runner,
				x,
				y,
				size,
				alpha: runner.count === 0 ? dimmed : 1,
				onTrack: true,
			});
		}
		return placed;
	}

	/**
	 * Start flight animations for anything new in the event log: blocks just
	 * launched fly from the column or tray slot they were clicked in onto the
	 * track, and blocks just stowed fly from the track into the tray. The
	 * events say where each one came from.
	 */
	private startFlights(state: GameState, layout: Layout, now: number): void {
		if (state !== this.flightState) {
			this.flightState = state;
			this.eventsSeen = 0;
		}
		const newEvents = state.events.slice(this.eventsSeen);
		const sources = newEvents.flatMap((e) =>
			e.type === "launch" ? e.sources : [],
		);
		this.eventsSeen = state.events.length;
		if (!this.reducedMotion && this.settings.flyMs > 0) {
			for (const event of newEvents) {
				if (event.type !== "stow") continue;
				for (const { block, position } of event.blocks) {
					const from = trackToPixel(layout, position, this.trackClosed());
					this.trayFlights.set(block, { from, start: now });
				}
			}
		}
		// New blocks appear on the track in the same order as their launch events, so they can be paired up in order.
		const fresh = state.runners.filter((r) => !this.seenRunners.has(r));
		for (const runner of fresh) this.seenRunners.add(runner);
		if (this.reducedMotion || this.settings.flyMs <= 0) return;

		const half = layout.blockSize / 2;
		fresh.forEach((runner, i) => {
			const source = sources[i];
			if (!source) return;
			const from =
				source.from === "column"
					? {
							x: layout.columnCenters[source.index] ?? 0,
							y: layout.columnsTop + half,
						}
					: {
							x: layout.trayCenters[source.index] ?? 0,
							y: layout.trayTop + half,
						};
			if (runner.wait > 0)
				this.flights.set(runner, { from, duration: runner.wait });
		});
	}

	/**
	 * Where (and how big) to draw a tray block: in its slot, or partway there if
	 * it's still flying in from the track. It grows from its track size to its
	 * tray size on the way.
	 */
	private flyToTray(
		block: Block,
		slot: { x: number; y: number },
		layout: Layout,
		now: number,
	): { x: number; y: number; size: number } {
		const flight = this.trayFlights.get(block);
		if (!flight) return { ...slot, size: layout.blockSize };
		const t = (now - flight.start) / this.settings.flyMs;
		if (t >= 1) {
			this.trayFlights.delete(block);
			return { ...slot, size: layout.blockSize };
		}
		const e = easeOut(Math.max(0, t));
		const size =
			layout.trackBlockSize + (layout.blockSize - layout.trackBlockSize) * e;
		return {
			x: flight.from.x + (slot.x - flight.from.x) * e,
			y: flight.from.y + (slot.y - flight.from.y) * e,
			// Swell slightly mid-flight, like a launch.
			size: size * (1 + 0.2 * Math.sin(Math.PI * t)),
		};
	}

	/**
	 * How long (in seconds) a launched block should pause at the start of the
	 * track: as long as its flight animation takes, or 0 with reduced motion.
	 */
	launchDelay(): number {
		return this.reducedMotion ? 0 : this.settings.flyMs / 1000;
	}

	/**
	 * Where to draw a block on the track: at its track position, or partway
	 * there if it's still flying in. The flight's progress comes from how much of
	 * the block's pause is left, so it lands exactly as it starts moving.
	 */
	private fly(
		runner: Runner,
		target: { x: number; y: number },
		layout: Layout,
	): { x: number; y: number; size: number } {
		const flight = this.flights.get(runner);
		if (!flight) return { ...target, size: layout.trackBlockSize };
		const t = 1 - runner.wait / flight.duration;
		if (t >= 1) {
			this.flights.delete(runner);
			return { ...target, size: layout.trackBlockSize };
		}
		// "Easing" makes movement start fast and slow down at the end, which looks
		// more natural than a constant speed. The block shrinks from its column
		// size to its track size on the way, swelling slightly mid-flight.
		const e = easeOut(t);
		const size =
			layout.blockSize + (layout.trackBlockSize - layout.blockSize) * e;
		return {
			x: flight.from.x + (target.x - flight.from.x) * e,
			y: flight.from.y + (target.y - flight.from.y) * e,
			size: size * (1 + 0.2 * Math.sin(Math.PI * t)),
		};
	}

	/**
	 * Move a tray block's drawn position a little closer to its slot each frame,
	 * covering the same fraction of the remaining distance each time. This
	 * "exponential" approach starts fast and settles gently. New blocks, and
	 * reduced motion, just jump straight to their slot.
	 */
	private slideTo(block: Block, target: number, dt: number): number {
		const previous = this.trayX.get(block);
		const { slideMs } = this.settings;
		let x = target;
		if (previous !== undefined && !this.reducedMotion && slideMs > 0) {
			x = target + (previous - target) * Math.exp((-4 * dt) / slideMs);
			if (Math.abs(x - target) < 0.5) x = target;
		}
		this.trayX.set(block, x);
		return x;
	}

	/**
	 * Under each column with more blocks than are drawn, show how many more are
	 * waiting, e.g. "+43", so deep columns don't shrink the whole board.
	 */
	private drawOverflow(state: GameState, layout: Layout): void {
		const { ctx } = this;
		ctx.fillStyle = this.colors.text;
		ctx.textAlign = "center";
		ctx.textBaseline = "top";
		// Sized from the column blocks, so it stays readable on big grids.
		ctx.font = `600 ${Math.round(Math.max(12, layout.blockSize * 0.4))}px system-ui, sans-serif`;
		state.columns.forEach((blocks, c) => {
			const hidden = blocks.length - layout.shownDepth;
			if (hidden <= 0) return;
			const y = layout.columnsTop + layout.shownDepth * layout.blockPitch;
			ctx.fillText(`+${hidden}`, layout.columnCenters[c] ?? 0, y);
		});
	}

	/** Draw a bar joining the blocks of each linked group, behind the blocks. */
	private drawLinks(placed: Placed[], layout: Layout): void {
		const { ctx } = this;
		const groups = new Map<number, Placed[]>();
		for (const p of placed) {
			if (p.block.link === undefined) continue;
			groups.set(p.block.link, [...(groups.get(p.block.link) ?? []), p]);
		}
		ctx.lineCap = "round";
		ctx.lineJoin = "round";
		for (const group of groups.values()) {
			if (group.length < 2) continue;
			// In proportion to the blocks it joins: blocks in the columns can be
			// much bigger than blocks on the track.
			const width = Math.max(4, (group[0]?.size ?? layout.blockSize) * 0.22);
			// As faded as its most faded block, so a bar joining dimmed blocks (not
			// launchable yet) doesn't stand out over them.
			ctx.globalAlpha = Math.min(...group.map((p) => p.alpha));
			// A dark (light, in the light theme) edge first, then the bar itself,
			// so the bar stands out against any block or background it crosses.
			for (const [color, extra] of [
				[this.colors.gridBackground, 3],
				[this.colors.text, 0],
			] as const) {
				ctx.strokeStyle = color;
				ctx.lineWidth = width + extra;
				ctx.beginPath();
				for (const p of group) ctx.lineTo(p.x, p.y);
				ctx.stroke();
			}
		}
		ctx.globalAlpha = 1;
	}

	/**
	 * The badge on a linked block: a chain link in a circle at the block's
	 * top-right corner. With linked blocks far apart (wide columns, or one in
	 * the tray and one on the track) the bar between them can be easy to miss;
	 * the badge says "linked" on every block, wherever it is.
	 */
	private drawLinkBadge(cx: number, cy: number, size: number): void {
		const { ctx } = this;
		const r = Math.max(6, size * 0.18);
		const x = cx + size / 2 - r * 0.55;
		const y = cy - size / 2 + r * 0.55;
		ctx.beginPath();
		ctx.arc(x, y, r, 0, Math.PI * 2);
		ctx.fillStyle = this.colors.text;
		ctx.fill();
		ctx.strokeStyle = this.colors.gridBackground;
		ctx.lineWidth = Math.max(1, r * 0.2);
		ctx.stroke();
		// Two interlocking links (rounded outlines), on a diagonal.
		ctx.save();
		ctx.translate(x, y);
		ctx.rotate(-Math.PI / 4);
		ctx.lineWidth = Math.max(1, r * 0.2);
		const long = r * 0.95;
		const short = r * 0.55;
		for (const shift of [-0.32, 0.32]) {
			roundRect(ctx, shift * r - long / 2, -short / 2, long, short, short / 2);
			ctx.stroke();
		}
		ctx.restore();
	}

	/** Draw one block centered on (`cx`, `cy`): its color, its icon, and its remaining count. */
	private drawBlock(
		block: Block,
		cx: number,
		cy: number,
		size: number,
		outline = false,
	): void {
		const { ctx } = this;
		const swatch = swatchFor(block.color);
		ctx.fillStyle = swatch.fill;
		roundRect(ctx, cx - size / 2, cy - size / 2, size, size, size * 0.2);
		ctx.fill();
		if (outline) {
			// Blocks on the track get a bold outline in the text color.
			ctx.strokeStyle = this.colors.text;
			ctx.lineWidth = 2;
			ctx.stroke();
		} else if (this.colors.outline) {
			// Light theme: a thin edge so light blocks stand out from the background.
			ctx.strokeStyle = this.colors.outline;
			ctx.lineWidth = Math.max(1, size * 0.04);
			ctx.stroke();
		}
		// The icon goes on top and the count below, so matching never depends on color alone.
		ctx.fillStyle = this.colors.ink;
		drawShape(ctx, swatch.shape, cx, cy - size * 0.19, size * 0.17);
		ctx.textAlign = "center";
		ctx.textBaseline = "middle";
		ctx.font = `bold ${Math.round(size * 0.38)}px system-ui, sans-serif`;
		ctx.fillText(String(block.count), cx, cy + size * 0.2);
		if (block.link !== undefined && size >= 18)
			this.drawLinkBadge(cx, cy, size);
	}

	/**
	 * The keyboard focus ring: a thick outline in the text color, with a thin
	 * outline in the grid's background color inside it, so it's visible
	 * against any block or background in either theme (keyboard focus must be
	 * visible: WCAG 2.4.7).
	 */
	private drawSelection(layout: Layout): void {
		const { selection } = this;
		if (!selection) return;
		const { ctx } = this;
		const size = layout.blockSize;
		const x = (
			selection.from === "column" ? layout.columnCenters : layout.trayCenters
		)[selection.index];
		if (x === undefined) return;
		const top =
			selection.from === "column" ? layout.columnsTop : layout.trayTop;
		const pad = Math.max(3, size * 0.12);
		roundRect(
			ctx,
			x - size / 2 - pad,
			top - pad,
			size + pad * 2,
			size + pad * 2,
			size * 0.25,
		);
		ctx.strokeStyle = this.colors.text;
		ctx.lineWidth = 3;
		ctx.stroke();
		roundRect(
			ctx,
			x - size / 2 - pad + 2.5,
			top - pad + 2.5,
			size + pad * 2 - 5,
			size + pad * 2 - 5,
			size * 0.22,
		);
		ctx.strokeStyle = this.colors.gridBackground;
		ctx.lineWidth = 1.5;
		ctx.stroke();
	}

	/** Draw the track counter, e.g. "3/5", below the track's bottom-left corner. It turns red when the track is full. */
	private drawHud(state: GameState, layout: Layout): void {
		const { ctx } = this;
		const full = !canLaunch(state) && state.status === "playing";
		ctx.fillStyle = full ? this.colors.alert : this.colors.text;
		ctx.textAlign = "left";
		ctx.textBaseline = "middle";
		// Between 14px and 26px: readable when grid cells are tiny, not huge when they're big.
		ctx.font = `bold ${Math.round(Math.min(26, Math.max(14, layout.cell * 0.5)))}px system-ui, sans-serif`;
		const { x, y } = layout.trackCounter;
		ctx.fillText(`${state.runners.length}/${state.maxRunners}`, x, y);
	}

	/** Draw the level title above the board. */
	private drawTitle(title: string, layout: Layout): void {
		if (!title) return;
		const { ctx } = this;
		ctx.fillStyle = this.colors.text;
		ctx.textAlign = "center";
		ctx.textBaseline = "middle";
		// Between 16px and 28px: readable when grid cells are tiny, not huge when they're big.
		ctx.font = `600 ${Math.round(Math.min(28, Math.max(16, layout.cell * 0.45)))}px system-ui, sans-serif`;
		ctx.fillText(title, layout.title.x, layout.title.y);
	}

	/** Draw the end-of-level overlay: "Cleared!" or why it was lost, and what clicking will do next. */
	private drawStatus(state: GameState, hud: Hud, w: number, h: number): void {
		const { ctx } = this;
		// Fade the board behind the message, using the page's own background
		// color (read from the CSS) so the two always match.
		ctx.fillStyle =
			getComputedStyle(this.canvas).getPropertyValue("--bg").trim() || "#000";
		ctx.globalAlpha = this.colors.overlayOpacity;
		ctx.fillRect(0, 0, w, h);
		ctx.globalAlpha = 1;
		ctx.fillStyle = this.colors.text;
		ctx.textAlign = "center";
		ctx.textBaseline = "middle";
		ctx.font = "bold 36px system-ui, sans-serif";
		ctx.fillText(
			state.status === "won"
				? hud.won
				: state.lossReason === "trayFull"
					? "Tray full"
					: "No moves left",
			w / 2,
			h / 2 - 20,
		);
		ctx.font = "18px system-ui, sans-serif";
		ctx.fillText(
			state.status === "won" ? hud.wonHint : hud.lostHint,
			w / 2,
			h / 2 + 24,
		);
	}
}

/**
 * Start a new path shaped like a rectangle with rounded corners. The caller
 * then fills or outlines it.
 */
function roundRect(
	ctx: CanvasRenderingContext2D,
	x: number,
	y: number,
	w: number,
	h: number,
	r: number,
): void {
	ctx.beginPath();
	ctx.roundRect(x, y, w, h, r);
}

/** An easing curve that starts fast and slows to a stop. Input and output both go from 0 to 1. */
function easeOut(t: number): number {
	return 1 - (1 - t) ** 3;
}

/** An easing curve that starts slowly and speeds up. Input and output both go from 0 to 1. */
function easeIn(t: number): number {
	return t * t;
}
